import { useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { userErrorMessage } from "@/lib/user-error";
import { Link } from "react-router";
import { BackButton } from "@/components/common/BackButton";
import { FilterChip } from "@/components/design/FilterChip";
import { LoadMore } from "@/components/common/LoadMore";
import { NoBackend } from "@/components/common/NoBackend";
import { ShareItemCardFeed } from "@/components/share/ShareItemCard";
import { ShareSortSelect } from "@/components/share/ShareSortSelect";
import { sortOrderFor, type SortKey } from "@/lib/share-sort";
import { usePagedQuery } from "@/lib/use-paged-query";
import { useAssistantContext } from "@/lib/use-assistant-context";
import { useAssistantTour } from "@/lib/use-assistant-tour";
import { SharedItemsDocument, type SharedItemsQuery } from "@/graphql/generated/operations";
import type { SharedItemType } from "@/graphql/generated/operations";

type SharedItemNode = SharedItemsQuery["sharedItems"]["nodes"][number];

// SHARE · Meine Beiträge - same ShareItemCardFeed template as the Share feed
// but scoped to the user's own items (owner filter). FilterChips at the top
// narrow the visible set by type (all / Projekte / Posts). Single-select with
// "All" as the no-filter default per the ambient-filter convention. Filtering
// is applied client-side so switching is instant and refetch-free.

const FILTERS = [
  { id: "all" as const, labelId: "share.mine.filterAll", labelDefault: "All" },
  { id: "PROJECT" as const, labelId: "share.mine.filterProjects", labelDefault: "Projects" },
  { id: "POST" as const, labelId: "share.mine.filterPosts", labelDefault: "Posts" },
];
type Filter = (typeof FILTERS)[number]["id"];

export function ShareMinePage() {
  const intl = useIntl();
  const [filter, setFilter] = useState<Filter>("all");
  const [sort, setSort] = useState<SortKey>("new");

  const input = useMemo(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    () => ({ order: sortOrderFor(sort), where: { owner: { eq: true } } } as any),
    [sort],
  );
  const {
    nodes,
    loading,
    error,
    hasMore,
    loadingMore,
    loadMoreFailed,
    loadMore,
  } = usePagedQuery<SharedItemNode>(SharedItemsDocument, {
    connectionKey: "sharedItems",
    input,
    pageSize: 24,
  });

  const items = useMemo(() => {
    if (filter === "all") return nodes;
    return nodes.filter((i) => i.itemType === (filter as SharedItemType));
  }, [nodes, filter]);

  // Live assistant context (motive 1, @external-adr AGENT-SERVICE-0020-ag-ui-protocol-adoption): the user's own shared items.
  useAssistantContext(
    loading && !nodes.length
      ? null
      : [
          {
            description: "the user's own shared items page",
            value:
              `${items.length} own shared items` +
              (filter !== "all" ? ` (filtered to ${filter})` : ""),
          },
        ],
  );

  // Guided tour (/tour): the user's own shared items — type filter + the items grid.
  useAssistantTour(
    loading && !nodes.length
      ? null
      : {
          label: intl.formatMessage({
            id: "share.mine.tour.label",
            defaultMessage: "Share Mine",
          }),
          summary: intl.formatMessage(
            {
              id: "share.mine.tour.summary",
              defaultMessage:
                "These are your own posts, everything you have published on Share. You have {count} items here right now.",
            },
            { count: items.length },
          ),
          stops: [
            {
              anchor: "share.mine.filters",
              title: intl.formatMessage({
                id: "share.mine.tour.filters.title",
                defaultMessage: "Filter & sort",
              }),
              description: intl.formatMessage({
                id: "share.mine.tour.filters.desc",
                defaultMessage:
                  "Narrow the list to Projects or Posts, and change the sort order on the right.",
              }),
            },
            {
              anchor: "share.mine.items",
              title: intl.formatMessage({
                id: "share.mine.tour.items.title",
                defaultMessage: "Your items",
              }),
              description:
                items.length === 0
                  ? intl.formatMessage({
                      id: "share.mine.tour.items.emptyDesc",
                      defaultMessage:
                        "Nothing here yet. Create a project or a post and it will show up in this grid.",
                    })
                  : intl.formatMessage(
                      {
                        id: "share.mine.tour.items.desc",
                        defaultMessage:
                          "{count} items. Click one to open it; as the owner you can edit or delete it from its detail page.",
                      },
                      { count: items.length },
                    ),
            },
          ],
        },
  );

  return (
    <div className="max-w-[1400px] px-4 pb-12 pt-2 @sm:px-6 @lg:px-10" data-graphql="SharedItems">
      <div className="mb-5">
        <BackButton />
      </div>
      <div className="mb-4 text-[10px] tracking-[0.25em] text-ink/55">
        <FormattedMessage id="share.mine.eyebrow" defaultMessage="SHARE · My posts" />
      </div>
      <h1 className="font-serif text-4xl leading-[0.95] tracking-tight @sm:text-5xl">
        <FormattedMessage id="share.mine.title" defaultMessage="My posts" />
      </h1>
      <p className="mt-4 max-w-md text-sm text-ink/70">
        <FormattedMessage id="share.mine.subtitle" defaultMessage="All your projects and posts." />
      </p>

      <div className="mt-6 flex flex-wrap items-center gap-2" data-assistant-anchor="share.mine.filters">
        {FILTERS.map((f) => (
          <FilterChip key={f.id} active={f.id === filter} onClick={() => setFilter(f.id)}>
            {intl.formatMessage({ id: f.labelId, defaultMessage: f.labelDefault })}
          </FilterChip>
        ))}
        <div className="ml-auto">
          <ShareSortSelect value={sort} onChange={setSort} />
        </div>
      </div>

      <div className="mt-8" data-assistant-anchor="share.mine.items">
        {loading ? (
          <div className="text-[11px] text-ink/45">
            <FormattedMessage id="common.loading" defaultMessage="Loading…" />
          </div>
        ) : error ? (
          <div className="text-[11px] text-ink/45">
            <FormattedMessage
              id="share.mine.error"
              defaultMessage="Error: {message}"
              values={{ message: userErrorMessage(intl, error) }}
            />
          </div>
        ) : items.length === 0 ? (
          <NoBackend
            label={intl.formatMessage({ id: "share.mine.emptyLabel", defaultMessage: "No own posts yet" })}
            hint={intl.formatMessage({ id: "share.mine.emptyHint", defaultMessage: "create a project or a post" })}
          />
        ) : (
          <>
            <div className="grid grid-cols-1 gap-4 @sm:grid-cols-2 @lg:grid-cols-4">
              {items.map((item, i) => (
                <Link
                  key={item.id}
                  to={`/share/discover/${item.id}`}
                  className="block transition hover:-translate-y-0.5"
                >
                  <ShareItemCardFeed item={item} index={i} />
                </Link>
              ))}
            </div>
            <LoadMore hasMore={hasMore} loading={loadingMore} paused={loadMoreFailed} onLoadMore={loadMore} />
          </>
        )}
      </div>
    </div>
  );
}
